import { t } from 'i18next';
import * as XlsxPopulate from 'xlsx-populate/browser/xlsx-populate';

declare global {
    interface Window {
        IQc: { entity: 'x', ENTITIES: { x: 'x'} };
    }
}

window.IQc = { entity: 'x', ENTITIES: { x: 'x'} };

import { lcidMap } from './Utils';
import { XLSXEntry } from './XLSXEntry';
import { XLSXTemplateInfo } from './XLSXTemplateInfo';

// Submodules
import { XLSXImage } from '../app-api/AppAction';
import { getInclusiveInfosList } from '../egr-gui-utils/CoreHelper';

import eaiws = egr.wcf.eaiws;
import utils = egr.wcf.utils;
import bsk = egr.wcf.bsk;

export const MimeType: string = XlsxPopulate.MIME_TYPE;

export interface ImagesInformation {
    snapshotStart: {row: number, col: number} | null;
}

export interface ExcelCell {
    value(newValue: number | string): ExcelCell;
    style(prop: string, value: string | number | boolean): ExcelCell;
    formula(value: string): ExcelCell;
    columnName(): string;
}

export interface ExcelRow {
    cell(index: number): ExcelCell;
}

export interface ExcelColumn {
    width(): number | undefined;
    width(value: number): void;
}

export interface ExcelSheet {
    row(index: number): ExcelRow;
    column(index: number): ExcelColumn;
}

export interface ExcelWorkbook {
    sheet(index: number): ExcelSheet;
    outputAsync(type: 'base64'): Promise<string | null>;
    outputAsync(type: 'arraybuffer'): Promise<ArrayBuffer | null>;
    outputAsync(): Promise<Blob | null>;
}

interface ImageDimensions {
    width: number;
    height: number;
}
export class XLSXDocument<T extends bsk.ViewItem> {
    public MIME_TYPE: string = XlsxPopulate.MIME_TYPE;
    public images: Array<XLSXImage> = [];
    protected contentList: Array<XLSXEntry<T>> = [];
    protected currencyFormat: string | undefined = undefined;

    constructor(
        articleItems: ReadonlyArray<T>,
        protected projectName: string,
        protected language: string,
        currency: string,
        protected showPrices: boolean,
        protected getSnapshotPaths: (item: T) => Promise<Array<string | null> | null>,
        protected imageDimensions: {articleImage: ImageDimensions, snapshot: ImageDimensions},
        protected addInclusiveInfos: boolean
    ) {
        this.projectName = projectName;
        for (const articleItem of articleItems) {
            const itemProperties: eaiws.basket.ItemProperties | undefined = articleItem.getCachedItemProperties();
            if (itemProperties !== undefined && itemProperties.article !== undefined) {
                const cachedCalculations: eaiws.basket.CalculationSheetInfo | undefined = articleItem.getCachedCalculation();
                this.contentList.push(new XLSXEntry<T>(
                    articleItem,
                    itemProperties.article,
                    itemProperties,
                    cachedCalculations
                ));
                if (articleItem.children.length) {
                    this.addChildren(articleItem.children as Array<T>);
                }
            }
        }

        if (showPrices) {
            this.currencyFormat = this.getCurrencyFormat(currency, language);
            this.showPrices = true;
        }
    }

    public async create(): Promise<ExcelWorkbook> {
        const templateBuffer: ArrayBuffer =  await utils.async.ajax<ArrayBuffer>('GET', './res/template.xlsx', null, { responseType: 'arraybuffer' });
        const template: ExcelWorkbook = await XlsxPopulate.fromDataAsync(templateBuffer);
        return this.fillWorkbook(template);
    }

    protected addChildren(articleItems: ReadonlyArray<T>): void {
        for (const articleItem of articleItems) {
            const itemProperties: eaiws.basket.ItemProperties | undefined = articleItem.getCachedItemProperties();
            if (itemProperties !== undefined && itemProperties.article !== undefined) {
                const cachedCalculations: eaiws.basket.CalculationSheetInfo | undefined = articleItem.getCachedCalculation();
                this.contentList.push(new XLSXEntry<T>(
                    articleItem,
                    itemProperties.article,
                    itemProperties,
                    cachedCalculations
                ));
                if (articleItem.children.length) {
                    this.addChildren(articleItem.children as Array<T>);
                }
            }
        }

    }

    protected addStandardHeaders(sheetOne: ExcelSheet, templateInfo: XLSXTemplateInfo): void {
        if (templateInfo.positionHeader != null) {
            sheetOne.row(templateInfo.rowHeader).cell(templateInfo.positionHeader).value(t('Pos.'));
        }
        if (templateInfo.imgHeader != null) {
            sheetOne.row(templateInfo.rowHeader).cell(templateInfo.imgHeader).value(t('Image'));
        }
        if (templateInfo.articleInformationHeader != null) {
            sheetOne.row(templateInfo.rowHeader).cell(templateInfo.articleInformationHeader).value(t('Article'));
        }
        if (templateInfo.manufacturerHeader != null) {
            sheetOne.row(templateInfo.rowHeader).cell(templateInfo.manufacturerHeader).value(t('Manufacturer'));
        }
        if (templateInfo.descriptionHeader != null) {
            sheetOne.row(templateInfo.rowHeader).cell(templateInfo.descriptionHeader).value(t('Description'));
        }
        if (templateInfo.quantityHeader != null) {
            sheetOne.row(templateInfo.rowHeader).cell(templateInfo.quantityHeader).value(t('Quantity'));
        }
    }

    protected addPriceHeaders(sheetOne: ExcelSheet, templateInfo: XLSXTemplateInfo): void {
        if (templateInfo.singlePriceHeader != null) {
            if (this.showPrices) {
                sheetOne.row(templateInfo.rowHeader).cell(templateInfo.singlePriceHeader).value(t('Single Price'));
            } else {
                sheetOne.row(templateInfo.rowHeader).cell(templateInfo.singlePriceHeader)
                    .value('').style('border', false);
            }
        }
        if (templateInfo.totalPriceHeader != null) {
            if (this.showPrices) {
                sheetOne.row(templateInfo.rowHeader).cell(templateInfo.totalPriceHeader).value(t('Total Price'));
            } else {
                sheetOne.row(templateInfo.rowHeader).cell(templateInfo.totalPriceHeader)
                    .value('').style('border', false);
            }
        }
    }

    protected addDocumentHeader(sheetOne: ExcelSheet, templateInfo: XLSXTemplateInfo): void {
        if (templateInfo.projectName != null) {
            sheetOne.row(templateInfo.projectName.row).cell(templateInfo.projectName.col)
                .value(utils.string.isNullOrEmpty(this.projectName) ? '' : this.projectName);
        }
        if (templateInfo.pageHeader != null) {
            sheetOne.row(templateInfo.pageHeader.row).cell(templateInfo.pageHeader.col)
                .value(t('Article List'));
        }
    }

    protected addEntryPosition(position: number, sheetOne: ExcelSheet, templateInfo: XLSXTemplateInfo): boolean {
        if (templateInfo.positionColumn != null) {
            sheetOne.row(templateInfo.row).cell(templateInfo.positionColumn)
                .value(position)
                .style('verticalAlignment', 'top')
                .style('wrapText', true);
            return true;
        }
        return false;
    }

    /**
     * Adds columns with correct widths of images, and also writes informations in global images array.
     * This global images array will be used by the App to add the images in the correct cells later.
     * @param entry
     * @param sheetOne
     * @param templateInfo
     */
    protected async addEntryImages(entry: XLSXEntry<T>, sheetOne: ExcelSheet, templateInfo: XLSXTemplateInfo): Promise<boolean> {
        if (
            this.getSnapshotPaths != null &&
            templateInfo.snapshot != null &&
            entry.item.item.basketItemIds != null &&
            entry.item.item.basketItemIds.length
        ) {
            const snapshotPaths: Array<string | null> | null = await this.getSnapshotPaths(entry.item);
            const snapshotColumn: number = templateInfo.snapshot;
            if (snapshotPaths != null) {
                snapshotPaths.forEach((snapshotPath: string | null, snapshotIndex: number) => {
                    if (utils.string.isNullOrEmpty(snapshotPath)) {
                        return;
                    }

                    let targetColumn: number = snapshotColumn + snapshotIndex;

                    if (templateInfo.mainImageColumn != null) {
                        targetColumn -= 1; // first image is placed in mainImageColumn
                        if (snapshotIndex === 0) {
                            targetColumn = templateInfo.mainImageColumn;
                        }
                    }

                    if (templateInfo.imageColumnWidth != null) {
                        sheetOne.column(targetColumn).width(templateInfo.imageColumnWidth);
                    }

                    if (utils.string.isNotNullOrEmpty(snapshotPath)) {
                        this.images.push({
                            row: templateInfo.row - 1,
                            col: targetColumn - 1,
                            path: snapshotPath,
                            maxwidth: this.imageDimensions.snapshot.width,
                            maxheight: this.imageDimensions.snapshot.height
                        });
                        sheetOne.row(templateInfo.row).cell(targetColumn).value('');
                    }
                });
            }
            sheetOne.row(templateInfo.row).cell(snapshotColumn).value('');
            return true;
        }
        return false;
    }

    protected addEntryTexts(entry: XLSXEntry<T>, sheetOne: ExcelSheet, templateInfo: XLSXTemplateInfo): boolean {
        let somethingAdded: boolean = false;
        if (templateInfo.articleInformationColumn != null && entry.articleInformation) {
            sheetOne.row(templateInfo.row).cell(
                templateInfo.articleInformationColumn
            ).value(entry.articleInformation).style('verticalAlignment', 'top').style('wrapText', true);
            somethingAdded = true;
        }

        if (templateInfo.manufacturerColumn != null && entry.manufacturer) {
                sheetOne.row(templateInfo.row).cell(
                    templateInfo.manufacturerColumn
                ).value(entry.manufacturer).style('verticalAlignment', 'top').style('wrapText', true);
                somethingAdded = true;
        }

        if (templateInfo.descriptionColumn != null) {
            let description: string = entry.description;
            if (this.addInclusiveInfos) {
                const infos: Array<string> = getInclusiveInfosList(entry.itemProperties, this.language, this.showPrices);
                if (infos.length) {
                    description = `${description}\n${t('Including additional component', {count: infos.length})}\n${infos.join('\n')}`;
                }
            }
            if (utils.string.isNotNullOrEmpty(description)) {
                sheetOne.row(templateInfo.row).cell(templateInfo.descriptionColumn)
                    .value(description)
                    .style('verticalAlignment', 'top')
                    .style('wrapText', true);
                somethingAdded = true;
            }
        }

        return somethingAdded;
    }

    protected addEntryQuantity(entry: XLSXEntry<T>, sheetOne: ExcelSheet, templateInfo: XLSXTemplateInfo): boolean {
        if (templateInfo.quantityColumn != null && entry.quantity) {
            sheetOne.row(templateInfo.row).cell(templateInfo.quantityColumn)
                .value(entry.quantity)
                .style('verticalAlignment', 'top')
                .style('wrapText', true);
            return true;
        }

        return false;
    }

    protected addEntryPrices(entry: XLSXEntry<T>, sheetOne: ExcelSheet, templateInfo: XLSXTemplateInfo): boolean {
        if (templateInfo.singlePriceColumn != null) {
            if (entry.singlePrice && this.currencyFormat !== undefined) {
                sheetOne.row(templateInfo.row).cell(templateInfo.singlePriceColumn)
                    .value(entry.singlePrice)
                    .style('verticalAlignment', 'top')
                    .style('wrapText', true)
                    .style('numberFormat', this.currencyFormat);
            } else {
                sheetOne.row(templateInfo.row).cell(templateInfo.singlePriceColumn).value('');
            }

            // totalprice is just calculated from quantity and singleprice values
            if (templateInfo.totalPriceColumn != null && templateInfo.quantityColumn != null) {
                if (entry.quantity && entry.singlePrice && this.currencyFormat !== undefined) {
                    const columnNameQuantity: string = sheetOne.row(templateInfo.row).cell(templateInfo.quantityColumn).columnName();
                    const columnNameSinglePrice: string = sheetOne.row(templateInfo.row).cell(templateInfo.singlePriceColumn).columnName();
                    sheetOne.row(templateInfo.row).cell(templateInfo.totalPriceColumn)
                        .formula(`${columnNameQuantity}${templateInfo.row}*${columnNameSinglePrice}${templateInfo.row}`)
                        .style('verticalAlignment', 'top')
                        .style('wrapText', true)
                        .style('numberFormat', this.currencyFormat);
                } else {
                    sheetOne.row(templateInfo.row).cell(templateInfo.totalPriceColumn).value('');
                }
            }
            return true;
        }

        return false;
    }

    protected addFinalRow(startRow: number, sheetOne: ExcelSheet, templateInfo: XLSXTemplateInfo): void {
        if (templateInfo.positionColumn != null) {
            sheetOne.row(templateInfo.row).cell(templateInfo.positionColumn)
                .value(t('Total', {context: 'label of final row in an (excel) table, make sure it can wrap if column has small size (by adding space or -)'}))
                .style('verticalAlignment', 'top')
                .style('wrapText', true);
            if (templateInfo.fontFamily && templateInfo.fontSize) {
                sheetOne.row(templateInfo.row).cell(templateInfo.positionColumn)
                    .style('fontSize', templateInfo.fontSize)
                    .style('fontFamily', templateInfo.fontFamily);
            }
        }
        this.addTotalPriceToFinalRow(startRow, sheetOne, templateInfo);
    }

    protected addTotalPriceToFinalRow(startRow: number, sheetOne: ExcelSheet, templateInfo: XLSXTemplateInfo): void {
        if (templateInfo.totalPriceColumn != null && this.showPrices && this.currencyFormat !== undefined) {
            if (templateInfo.descriptionColumn != null && this.showPrices) {
                sheetOne.row(templateInfo.row).cell(templateInfo.descriptionColumn)
                    .value(t('Price (excl. Tax)'))
                    .style('verticalAlignment', 'top')
                    .style('wrapText', true);
                if (templateInfo.fontFamily && templateInfo.fontSize) {
                    sheetOne.row(templateInfo.row).cell(templateInfo.descriptionColumn)
                        .style('fontSize', templateInfo.fontSize)
                        .style('fontFamily', templateInfo.fontFamily);
                }
            }

            const columnName: string = sheetOne.row(templateInfo.row).cell(templateInfo.totalPriceColumn).columnName();
            sheetOne.row(templateInfo.row).cell(templateInfo.totalPriceColumn)
                .formula(`SUM(${columnName}${startRow}:${columnName}${templateInfo.row - 1})`)
                .style('verticalAlignment', 'top')
                .style('wrapText', true)
                .style('numberFormat', this.currencyFormat);
        }
    }

    protected fillWorkbook = async (workbook: ExcelWorkbook): Promise<ExcelWorkbook> => {
        const sheetOne: ExcelSheet = workbook.sheet(0);
        const templateInfo: XLSXTemplateInfo = new XLSXTemplateInfo(sheetOne);

        if (templateInfo.rowHeader != null) {
            this.addStandardHeaders(sheetOne, templateInfo);
            this.addPriceHeaders(sheetOne, templateInfo);
        }

        this.addDocumentHeader(sheetOne, templateInfo);

        const startRow: number = templateInfo.row;
        this.images = [];

        for (let index: number = 0; index < this.contentList.length; index++) {
            const entry: XLSXEntry<T> = this.contentList[index];

            let lineAdded: boolean = this.addEntryPosition(index + 1, sheetOne, templateInfo);

            lineAdded = await this.addEntryImages(entry, sheetOne, templateInfo) || lineAdded;

            lineAdded = this.addEntryTexts(entry, sheetOne, templateInfo) || lineAdded;

            lineAdded = this.addEntryQuantity(entry, sheetOne, templateInfo) || lineAdded;

            lineAdded = this.addEntryPrices(entry, sheetOne, templateInfo) || lineAdded;

            if (lineAdded) {
                templateInfo.row += 1;
            }
        }

        this.addFinalRow(startRow, sheetOne, templateInfo);
        return workbook;
    };

    protected getCurrencyFormat(currency: string, language: string): string {
        const lcid: string = lcidMap[language] !== undefined ? lcidMap[language] : lcidMap.de;
        return '#,##0.00\ [$' + currency + '-' + lcid + '];[RED]\-#,##0.00\ [$' + currency + '-' + lcid + ']';
    }

}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/submodules/articlelist-export/XLSXDocument.ts